import React, { useState, useEffect } from 'react';
import { Upload, HelpCircle, AlertCircle, FileText, ChevronLeft, ChevronRight, Eye, X, ChevronDown } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';

const isEnglishOnly = (str) => {
  if (!str) return true;
  return /^[\x20-\x7E\s]*$/.test(str);
};

const SupportTickets = () => {
  const { toast } = useToast();
  
  // Form states
  const [message, setMessage] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Tickets list states
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const fetchMyTickets = async (page = 0, size = pageSize) => {
    setLoading(true);
    try {
      const res = await vendorService.getMySupportTickets(page, size);
      if (res.success && res.data) {
        setTickets(res.data.tickets || []);
        setCurrentPage(res.data.currentPage || 0);
        // Calculate total pages based on total items if backend totalPages is missing
        const calculatedTotalPages = Math.ceil((res.data.totalItems || 0) / size);
        setTotalPages(res.data.totalPages || calculatedTotalPages || 0);
        setTotalItems(res.data.totalItems || 0);
      }
    } catch (err) {
      console.error("Error fetching support tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTickets(0, pageSize);
  }, [pageSize]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select a valid image file.");
      return;
    }

    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
  };

  const [touched, setTouched] = useState(false);
  const messageError = !message.trim()
    ? "Message details are required."
    : (message.trim().length < 10 
        ? "Message details must be at least 10 characters." 
        : (!isEnglishOnly(message) 
            ? "Please use English characters only (emojis and non-English scripts are not allowed)." 
            : null));

  useEffect(() => {
    if (!isFormOpen) {
      setTouched(false);
    }
  }, [isFormOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (messageError) {
      toast.warning("Please correct the errors in the form.");
      const el = document.getElementsByName('message')[0];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('description', message);
      formData.append('priority', 'MEDIUM');
      if (photo) {
        formData.append('photo', photo);
      }

      const res = await vendorService.createSupportTicket(formData);
      if (res.success) {
        toast.success("Support ticket raised successfully! The admin will review it shortly.");
        setMessage('');
        setTouched(false);
        handleRemovePhoto();
        setIsFormOpen(false);
        fetchMyTickets(0);
      } else {
        toast.error("Failed to submit ticket: " + res.error);
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      fetchMyTickets(newPage, pageSize);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);
      
      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);
      
      if (currentPage <= 1) {
        end = 3;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 4;
      }
      
      if (start > 1) {
        pages.push('ellipsis1');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 2) {
        pages.push('ellipsis2');
      }
      
      pages.push(totalPages - 1);
    }
    return pages;
  };

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 pb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Raise Ticket</h2>
          <p className="text-xs text-gray-400 mt-1 font-medium">Submit operational, listing, profile, or membership queries to the admin.</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="flex-shrink-0 bg-[#2ab391] hover:bg-[#23977a] text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-xs cursor-pointer text-center text-sm flex items-center justify-center gap-2"
        >
          <HelpCircle size={16} />
          Raise Ticket
        </button>
      </div>

      {/* Modal overlay */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xl w-full max-w-lg relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-lg transition-colors cursor-pointer"
              title="Close Form"
            >
              <X size={18} />
            </button>

            <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-1.5">
              <HelpCircle size={18} className="text-[#2ab391]" />
              Raise Ticket
            </h3>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Description Message */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                  Message details <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onBlur={() => setTouched(true)}
                  rows="4"
                  placeholder="Please describe your issue or question in detail..."
                  className={`w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none text-gray-700 placeholder-gray-400 font-medium resize-none leading-relaxed focus:ring-2 ${
                    touched && messageError
                      ? 'border-2 border-red-400 focus:ring-red-300 text-red-700'
                      : 'border-gray-300 focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391]'
                  }`}
                ></textarea>
                {touched && messageError && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {messageError}
                  </p>
                )}
              </div>

              {/* Screenshot upload zone */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                  Attach Screenshot (optional)
                </label>
                
                {!photoPreview ? (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-[#2ab391] rounded-xl py-6 bg-white cursor-pointer transition-colors group">
                    <Upload className="w-8 h-8 text-gray-400 group-hover:text-[#2ab391] transition-colors mb-2" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-[#2ab391] transition-colors">Click to Upload Image</span>
                    <span className="text-[10px] text-gray-400 mt-1">PNG, JPG, JPEG up to 5MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-white p-2 flex items-center justify-between gap-3 shadow-xs">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <img
                        src={photoPreview}
                        alt="Attachment Preview"
                        className="w-12 h-12 object-cover rounded-md border border-gray-100 flex-shrink-0"
                      />
                      <div className="overflow-hidden">
                        <p className="text-xs font-bold text-gray-700 truncate max-w-[150px]">{photo ? photo.name : 'attachment.jpg'}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Ready to upload</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="p-1.5 hover:bg-rose-50 text-rose-500 rounded-lg border border-transparent hover:border-rose-100 transition-colors cursor-pointer mr-1"
                      title="Remove Photo"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Submit btn */}
              <div className="pt-2 flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#2ab391] hover:bg-[#23977a] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-xl transition-all shadow-xs cursor-pointer text-center text-sm"
                >
                  {submitting ? 'Sending Ticket...' : 'Raise Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tickets List Column - Full Width */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-gray-700 flex items-center gap-1.5">
          <FileText size={18} className="text-gray-400" />
          Support Tickets History
          <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full ml-1">{totalItems} filed</span>
        </h3>

        {loading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-2xl py-16 text-center bg-white">
            <AlertCircle size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-500">No support tickets submitted yet.</p>
            <p className="text-xs text-gray-400 mt-1">If you have any queries, feel free to submit them using the button above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="divide-y divide-gray-100 bg-white border border-gray-200/80 rounded-2xl shadow-xs overflow-hidden">
              {tickets.map(t => (
                <div key={t.id} className="p-5 hover:bg-gray-50/30 transition-colors flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-bold text-gray-400">#{t.id}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                        t.status === 'SOLVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                        t.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                        'bg-amber-50 text-amber-700 border border-amber-100'
                      }`}>
                        {t.status === 'SOLVED' ? 'Solved' : t.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending'}
                      </span>
                    </div>

                    {t.ticket_type === 'CATEGORY_REQUEST' ? (
                      <div className="space-y-1 mt-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="px-2 py-0.5 text-[9px] font-bold rounded uppercase bg-teal-50 text-teal-700 border border-teal-100">Category Request</span>
                          <span className="text-xs font-bold text-gray-800">Requested: <span className="text-[#2ab391] font-bold">{t.requested_category}</span></span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed max-w-xl whitespace-pre-wrap"><span className="font-semibold text-gray-600">Reason:</span> {t.description}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 leading-relaxed max-w-xl whitespace-pre-wrap">{t.description}</p>
                    )}
                    
                    <p className="text-[10px] text-gray-600 font-semibold">Submitted on {new Date(t.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}</p>
                  </div>

                  {/* Screenshot thumbnail */}
                  {t.photo_url && (
                    <a
                      href={getImageUrl(t.photo_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-shrink-0 relative group rounded-lg overflow-hidden border border-gray-150 p-1 bg-white hover:border-[#2ab391] transition-all flex items-center justify-center"
                    >
                      <img
                        src={getImageUrl(t.photo_url)}
                        alt="Screenshot"
                        className="w-14 h-14 object-cover rounded"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                        <Eye size={12} />
                      </div>
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6">
              {/* Left: Entries Info */}
              <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
                {totalItems === 0 ? (
                  'No records available'
                ) : (
                  `Showing ${currentPage * pageSize + 1} to ${Math.min((currentPage + 1) * pageSize, totalItems)} of ${totalItems} entries`
                )}
              </div>

              {/* Right: Rows per page and Page buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
                {/* Rows per page Selector */}
                <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(0);
                      }}
                      className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                      <ChevronDown size={12} />
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
                </div>

                {/* Pagination Buttons */}
                {totalPages > 0 && (
                  <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                    >
                      Previous
                    </button>

                    {/* Page numbers - Hidden on mobile, visible on sm and larger */}
                    <div className="hidden sm:flex items-center gap-1">
                      {getPageNumbers().map((pageNum, idx) => {
                        if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                          return (
                            <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                              ...
                            </span>
                          );
                        }
                        const isActive = currentPage === pageNum;
                        return (
                          <button
                            key={`page-${pageNum}`}
                            type="button"
                            onClick={() => handlePageChange(pageNum)}
                            className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                              isActive
                                ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum + 1}
                          </button>
                        );
                      })}
                    </div>

                    {/* Page indicator text - Visible only on mobile */}
                    <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                      {currentPage + 1} / {totalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTickets;
